import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';

import type {
  IScheduledMessage,
  IScheduledMessagesListRes,
} from 'infrastructure/interfaces';

interface Props {
  conversationId: number | string;
  onSuccess?: () => void;
}
interface FnProps {
  conversationId: number | string;
  messageId: number | string;
}

export const cancelScheduledMessageFn = async (props: FnProps) => {
  const API_SCHEDULED_MESSAGES = `core-2/conversations/scheduled-messages`;

  const { data, error } = await dataService.deleteObject(
    API_SCHEDULED_MESSAGES,
    { conversationId: props.conversationId, messageId: props.messageId },
  );

  if (error) {
    showResult('Unable to retrieve scheduled messages');
    throw new Error(error);
  }

  return data;
};

export const cancelScheduledMessageKey = (props: Props) => {
  const copy = structuredClone(props);
  return ['getSmsDashboardScheduledMessages', copy];
};

export const useCancelScheduledMessage = (props: Props) => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: cancelScheduledMessageFn,
    onSuccess(data, variables) {
      const scheduledMessagesKey = cancelScheduledMessageKey({
        conversationId: props.conversationId,
      });
      queryClient.setQueryData(
        scheduledMessagesKey,
        (oldMessages: IScheduledMessagesListRes | undefined) => {
          if (!oldMessages) return;
          return {
            ...oldMessages,
            items: oldMessages.items.filter(
              (message: IScheduledMessage) =>
                message.messageId !== variables.messageId,
            ),
          };
        },
      );
      if (props.onSuccess) {
        props.onSuccess();
      }
    },
  });

  return {
    cancelScheduledMessage: mutate,
    cancelScheduledMessageLoading: isLoading,
    ...other,
  };
};
